/**
 * Ad blocker detection module
 * Provides methods to detect various ad blockers across different browsers
 */

/**
 * Detects ad blockers across various browsers and ad blocking tools
 * Specifically tested with:
 * - Brave browser's built-in shields
 * - Edge browser's tracking prevention (strict mode)
 * - uBlock Origin
 * - AdBlock Plus
 * - AdGuard
 * 
 * @returns {Promise<{detected: boolean, type: string|null}>} Detection result and type if identified
 */
export async function detectAdBlocker() {
    try {
        console.debug('TNCMS.Access: Ad blocker detection started');

        // First check for browser-specific ad blocking
        const oBrowserInfo = detectBrowser();
        const bBraveDetected = await detectBraveShields();

        if (bBraveDetected) {
            console.debug('TNCMS.Access: Brave Shields detected');
            return { detected: true, type: 'brave' };
        }

        // Create container with random ID to avoid pattern matching
        const sRandomId = 'ad-container-' + Math.random().toString(36).substring(2, 15);
        const oContainer = document.createElement('div');
        oContainer.id = sRandomId;
        oContainer.style.cssText = 'position: absolute; left: -9999px; top: -9999px; width: 300px; height: 250px;';
        document.body.appendChild(oContainer);

        // Create diverse baits that target different ad blockers

        // Bait 1: Common ad classes (uBlock Origin, AdBlock Plus)
        const oBait1 = document.createElement('div');
        oBait1.className = 'ad_wrapper adsbox ad-placement';
        oBait1.style.cssText = 'height: 10px; width: 10px; display: block !important; visibility: visible !important;';
        oBait1.textContent = 'advertisement';
        oContainer.appendChild(oBait1);

        // Bait 2: Ad frame (uBlock Origin, Edge tracking prevention)
        const oBait2 = document.createElement('div');
        oBait2.id = 'ad_frame';
        oBait2.className = 'ad-frame';
        oBait2.style.cssText = 'height: 10px; width: 10px; display: block !important; visibility: visible !important;';
        oBait2.textContent = 'sponsored';
        oContainer.appendChild(oBait2);

        // Bait 3: Google Ads format (AdGuard, Edge tracking prevention)
        const oBait3 = document.createElement('div');
        oBait3.id = 'google_ads_iframe';
        oBait3.className = 'adsbygoogle';
        oBait3.setAttribute('data-ad-client', 'ca-pub-1234567890');
        oBait3.setAttribute('data-ad-slot', '1234567890');
        oBait3.style.cssText = 'height: 10px; width: 10px; display: block !important; visibility: visible !important;';
        oBait3.textContent = 'advertisement';
        oContainer.appendChild(oBait3);

        // Bait 4: Ad unit (uBlock Origin)
        const oBait4 = document.createElement('div');
        oBait4.id = 'ad-unit';
        oBait4.className = 'ad-unit';
        oBait4.style.cssText = 'width: 10px; height: 10px; display: block !important; visibility: visible !important;';
        oBait4.textContent = 'sponsored content';
        oContainer.appendChild(oBait4);

        // Bait 5: Ad network (AdBlock Plus, AdGuard)
        const oBait5 = document.createElement('div');
        oBait5.className = 'adnetwork-ad-unit';
        oBait5.setAttribute('data-ad-slot', '1234567890');
        oBait5.style.cssText = 'height: 10px; width: 10px; display: block !important; visibility: visible !important;';
        oBait5.textContent = 'advertisement';
        oContainer.appendChild(oBait5);

        // Bait 6: Facebook ads (targets social media ad blockers)
        const oBait6 = document.createElement('div');
        oBait6.className = 'fb-ad';
        oBait6.setAttribute('data-ad-format', 'rectangle');
        oBait6.style.cssText = 'height: 10px; width: 10px; display: block !important; visibility: visible !important;';
        oContainer.appendChild(oBait6);

        // Give time to process
        await new Promise(resolve => setTimeout(resolve, 100));

        // Check for uBlock's shadow DOM elements
        const oUBlockShadowRoot = document.querySelector('html > :not(head):not(body)');
        if (oUBlockShadowRoot && oUBlockShadowRoot.shadowRoot) {
            console.debug('TNCMS.Access: Detected uBlock Origin shadow DOM element');
            document.body.removeChild(oContainer);
            return { detected: true, type: 'ublock' };
        }

        // Check which baits were affected
        const aBaits = [oBait1, oBait2, oBait3, oBait4, oBait5, oBait6];
        let aHiddenBaits = [];

        for (let i = 0; i < aBaits.length; i++) {
            const oBait = aBaits[i];
            const nBaitNum = i + 1;

            // Check if element was removed
            if (!oBait.parentNode) {
                console.debug(`TNCMS.Access: Ad blocker bait ${nBaitNum} was removed from DOM`);
                aHiddenBaits.push(nBaitNum);
                continue;
            }

            // Check if element was hidden
            const oStyle = window.getComputedStyle(oBait);
            if (oStyle.display === 'none' ||
                oStyle.visibility === 'hidden' ||
                oStyle.opacity === '0' ||
                (parseInt(oStyle.height) === 0 && parseInt(oStyle.width) === 0)) {
                console.debug(`TNCMS.Access: Ad blocker bait ${nBaitNum} was hidden by CSS`);
                aHiddenBaits.push(nBaitNum);
                continue;
            }
        }

        // Clean up
        document.body.removeChild(oContainer);

        // Identify specific ad blockers based on patterns

        // uBlock Origin typically hides baits 1, 2, and 4
        const bUBlockPattern = aHiddenBaits.includes(1) && aHiddenBaits.includes(2) && aHiddenBaits.includes(4);

        // Edge tracking prevention typically affects baits 2, 3, and 5
        const bEdgePattern = oBrowserInfo.isEdge &&
            aHiddenBaits.includes(2) &&
            aHiddenBaits.includes(3) &&
            aHiddenBaits.includes(5);

        // AdBlock Plus typically affects baits 1, 3, and 5
        const bAdBlockPlusPattern = aHiddenBaits.includes(1) &&
            aHiddenBaits.includes(3) &&
            aHiddenBaits.includes(5);

        // Determine the type of ad blocker
        let sType = null;

        if (bUBlockPattern) {
            sType = 'ublock';
        } else if (bEdgePattern) {
            sType = 'edge';
        } else if (bAdBlockPlusPattern) {
            sType = 'adblock-plus';
        } else if (aHiddenBaits.length >= 3) {
            sType = 'generic';
        }

        const bDetected = aHiddenBaits.length >= 3;
        console.debug(`TNCMS.Access: Ad blocker detection: ${aHiddenBaits.length} baits hidden (${aHiddenBaits.join(', ')}), type: ${sType}, result: ${bDetected}`);

        return { detected: bDetected, type: sType };
    } catch (oError) {
        console.debug('TNCMS.Access: Ad blocker detection error', oError);
        return { detected: false, type: null };
    }
}

/**
 * Detects browser information
 * @returns {Object} Browser information
 */
function detectBrowser() {
    const sUserAgent = navigator.userAgent;
    const bIsChrome = /Chrome/.test(sUserAgent) && /Google Inc/.test(navigator.vendor);
    const bIsFirefox = /Firefox/.test(sUserAgent);
    const bIsEdge = /Edg/.test(sUserAgent);
    const bIsSafari = /Safari/.test(sUserAgent) && !/Chrome/.test(sUserAgent);
    const bIsBrave = bIsChrome && navigator.brave?.isBrave;

    return {
        isChrome: bIsChrome,
        isFirefox: bIsFirefox,
        isEdge: bIsEdge,
        isSafari: bIsSafari,
        isBrave: bIsBrave
    };
}

/**
 * Specifically detects Brave browser's shields without loading third-party resources
 * @returns {Promise<boolean>} True if Brave shields are likely enabled
 */
async function detectBraveShields() {
    // Check if we're in Brave browser
    if (navigator.brave && typeof navigator.brave.isBrave === 'function') {
        try {
            // Confirm it's Brave
            const bIsBrave = await navigator.brave.isBrave();
            if (!bIsBrave) return false;

            // Create a local test element that Brave Shields typically blocks
            const oContainer = document.createElement('div');
            oContainer.style.cssText = 'position: absolute; left: -9999px; top: -9999px;';
            document.body.appendChild(oContainer);

            // Create elements that Brave Shields typically blocks
            const oTrackingPixel = document.createElement('img');
            oTrackingPixel.className = 'tracking-pixel';
            oTrackingPixel.style.cssText = 'width: 1px; height: 1px;';
            // Use a data URI instead of an external URL
            oTrackingPixel.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==';
            oContainer.appendChild(oTrackingPixel);

            const oFingerprintDiv = document.createElement('div');
            oFingerprintDiv.id = 'fingerprint-detector';
            oFingerprintDiv.setAttribute('data-fingerprint', 'true');
            oContainer.appendChild(oFingerprintDiv);

            // Create a fake ad element
            const oAdElement = document.createElement('div');
            oAdElement.className = 'brave-test-ad';
            oAdElement.setAttribute('data-ad-client', 'test');
            oAdElement.setAttribute('data-ad-slot', 'test');
            oAdElement.style.cssText = 'width: 300px; height: 250px; background: #f0f0f0;';
            oAdElement.textContent = 'Advertisement';
            oContainer.appendChild(oAdElement);

            // Give Brave time to process
            await new Promise(resolve => setTimeout(resolve, 50));

            // Check if elements were affected
            const oAdStyle = window.getComputedStyle(oAdElement);
            const bAdBlocked = oAdElement.offsetHeight === 0 ||
                oAdStyle.display === 'none' ||
                oAdStyle.visibility === 'hidden';

            // Clean up
            document.body.removeChild(oContainer);

            // If the ad element was blocked, Brave shields are likely enabled
            return bAdBlocked;
        } catch (oError) {
            console.debug('TNCMS.Access: Brave detection error', oError);
            return false;
        }
    }

    return false;
}
